'use client';

import React, { useMemo } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faAnglesRight,
  faAnglesLeft,
  faAnglesUp,
  faAnglesDown,
} from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';

interface DrawerToggleButtonProps {
  isOpen: boolean;
  onClick: () => void;
  anchor: 'left' | 'right' | 'top' | 'bottom';
  position?: 'start' | 'center' | 'end';
  className?: string;
}

const DrawerToggleButton: React.FC<DrawerToggleButtonProps> = ({
  isOpen,
  onClick,
  anchor,
  position = 'center',
  className = '',
}) => {
  const icon = useMemo(() => {
    // When drawer is open, show the icon that indicates closing direction
    if (isOpen) {
      return {
        right: faAnglesRight, // Point right to close
        left: faAnglesLeft, // Point left to close
        top: faAnglesUp, // Point up to close
        bottom: faAnglesUp, // Point up to close
      }[anchor];
    }
    // When drawer is closed, show the icon that indicates opening direction
    return {
      right: faAnglesLeft, // Point left to open
      left: faAnglesRight, // Point right to open
      top: faAnglesDown, // Point down to open
      bottom: faAnglesDown, // Point down to open
    }[anchor];
  }, [isOpen, anchor]);

  // Generate position classes based on drawer state and anchor
  const getPositionClasses = () => {
    const classes = ['z-50'];

    // Determine edge positioning based on anchor and open state
    if (!isOpen) {
      // When drawer is closed, position at the edge of the screen
      if (anchor === 'left') {
        classes.push('-left-1');
      } else if (anchor === 'right') {
        classes.push('-right-1');
      } else if (anchor === 'top') {
        classes.push('top-0');
      } else if (anchor === 'bottom') {
        classes.push('bottom-0');
      }
    } else {
      // When drawer is open, position at the drawer's edge
      if (anchor === 'left') {
        classes.push('right-0 translate-x-full');
      } else if (anchor === 'right') {
        classes.push('left-0 -translate-x-full');
      } else if (anchor === 'top') {
        classes.push('bottom-2.5');
      } else if (anchor === 'bottom') {
        classes.push('top-2.5');
      }
    }

    // Vertical/horizontal positioning
    const isVertical = anchor === 'left' || anchor === 'right';
    if (isVertical) {
      if (position === 'start') {
        classes.push('top-10');
      } else if (position === 'end') {
        classes.push('bottom-10');
      } else {
        classes.push('top-1/2', '-translate-y-1/2');
      }
    } else {
      if (position === 'start') {
        classes.push('left-24');
      } else if (position === 'end') {
        classes.push('right-24');
      } else {
        classes.push('left-1/2', '-translate-x-1/2');
      }
    }

    return classes.join(' ');
  };

  return (
    <button
      onClick={onClick}
      aria-label={isOpen ? 'Close drawer' : 'Open drawer'}
      data-testid="drawer-toggle-button"
      className={twMerge(
        getPositionClasses(),
        'w-7 h-12 rounded-l-lg rounded-r-none',
        'bg-white hover:bg-neutral-100',
        'flex items-center justify-center',
        'text-neutral-600 hover:text-neutral-700',
        'shadow-md focus:outline-none',
        'transition-all duration-300 ease-in-out',
        className
      )}
    >
      <Icon icon={icon} className="text-sm text-neutral-600" />
    </button>
  );
};

export default DrawerToggleButton;
